import { type ReactNode } from "react";

import { type SelectItem } from "@fronterahealth/frontera-ui-components";

import { type CreateOrganizationInput } from "@api/rest/types-and-hooks";
import { DoubleColumnContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { VERTICAL_ORG_ID_COPY, VERTICAL_ORG_ID_PATTERN } from "@pages/Organizations/constants";
import {
  CREATABLE_ORGANIZATION_TYPES,
  getOrganizationTypeLabel,
  takesVerticalOrgId,
} from "@pages/Organizations/ehrSync";

const ORGANIZATION_TYPE_ITEMS: SelectItem[] = CREATABLE_ORGANIZATION_TYPES.map((value) => ({
  id: value,
  primary: getOrganizationTypeLabel(value),
}));

interface OrganizationFormFieldsProps {
  formUtils: ReturnType<typeof useFormUtils<CreateOrganizationInput>>;
  /** Rendered beside EHR system, which it depends on. */
  organizationTypeAddon?: ReactNode;
  /** Shows the vertical organization id for Frontera EHR; only callers that send it should opt in. */
  showVerticalOrgId?: boolean;
}

export const OrganizationFormFields: React.FC<OrganizationFormFieldsProps> = ({
  formUtils,
  organizationTypeAddon,
  showVerticalOrgId = false,
}) => {
  const { RegisteredFormInput, RegisteredFormSelected, watch } = formUtils;

  return (
    <>
      <DoubleColumnContainer>
        <RegisteredFormInput
          formKey="name"
          label="Name"
          helpText="This is any human-readable identifier for the organization"
        />
        <RegisteredFormInput
          formKey="display_name"
          label="Display name"
          helpText="This name is displayed to end-users"
          required={false}
        />
      </DoubleColumnContainer>

      <DoubleColumnContainer>
        <RegisteredFormSelected
          formKey="organization_type"
          title="EHR system"
          placeholderText="Select an EHR..."
          items={ORGANIZATION_TYPE_ITEMS}
        />
        {organizationTypeAddon}
      </DoubleColumnContainer>

      {showVerticalOrgId && takesVerticalOrgId(watch("organization_type")) && (
        <DoubleColumnContainer>
          <RegisteredFormInput
            formKey="external_id"
            label={VERTICAL_ORG_ID_COPY.label}
            helpText={VERTICAL_ORG_ID_COPY.help}
            placeholder="00000000-0000-0000-0000-000000000000"
            required={false}
            pattern={VERTICAL_ORG_ID_PATTERN}
            patternMessage={VERTICAL_ORG_ID_COPY.invalid}
          />
        </DoubleColumnContainer>
      )}

      <DoubleColumnContainer>
        <RegisteredFormInput formKey="hubspot_company_id" label="Hubspot company ID" required={false} />
        <RegisteredFormInput formKey="logo_url" label="Logo URL" required={false} />
      </DoubleColumnContainer>

      <DoubleColumnContainer>
        <RegisteredFormInput formKey="primary_color" label="Primary color" placeholder="#000000" required={false} />
        <RegisteredFormInput
          formKey="background_color"
          label="Background color"
          placeholder="#ffffff"
          required={false}
        />
      </DoubleColumnContainer>
    </>
  );
};
